import { useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "@/components/auth/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { updateRecoveryPassword } from "@/lib/apiClient";
import { PASSWORD_POLICY_DESCRIPTION, passwordPolicyError } from "@/lib/passwordPolicy";

export default function ResetPassword() {
  const { session, loading, updatePassword, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const policyError = passwordPolicyError(password);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    const { error: err } = await updatePassword(password);
    let finalError = err;
    if (isAal2PasswordUpdateError(err)) {
      const recovery = await updateRecoveryPassword(password);
      finalError = recovery?.ok ? null : { message: recovery?.message || "Could not change password from this reset link." };
    }
    setSubmitting(false);
    if (finalError) {
      setError(finalError.message ?? "Could not change password.");
      return;
    }
    await signOut({ scope: "global" });
    setPassword("");
    setConfirmPassword("");
    setSaved(true);
  }

  if (loading) {
    return (
      <AuthLayout title="Checking reset link" subtitle="Loading your password reset session.">
        <div className="text-sm text-slate-500">Loading...</div>
      </AuthLayout>
    );
  }

  if (saved) {
    return (
      <AuthLayout
        title="Password changed"
        subtitle="All sessions for this account were signed out."
        footer={
          <Link data-testid="reset-password-go-login" to="/login" className="text-teal-700 font-medium hover:underline">Return to sign in</Link>
        }
      >
        <p data-testid="reset-password-success" className="rounded-lg border border-teal-100 bg-teal-50 px-3 py-2 text-sm text-teal-800">
          Password changed. For your security, all sessions were signed out. Please sign in again.
        </p>
      </AuthLayout>
    );
  }

  if (!session) {
    return (
      <AuthLayout
        title="Reset link expired"
        subtitle="Request a fresh password reset link from the login page."
        footer={
          <Link data-testid="reset-password-back-login" to="/login" className="text-teal-700 font-medium hover:underline">Back to sign in</Link>
        }
      >
        <p data-testid="reset-password-expired" className="text-sm text-slate-600">
          For security, password reset links only work once and expire after a short time.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="This will update the password for your Dental OS account."
      footer={
        <Link data-testid="reset-password-go-login" to="/login" className="text-teal-700 font-medium hover:underline">Return to sign in</Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="reset-password">New password</label>
          <input
            id="reset-password"
            data-testid="reset-password-input"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
            placeholder="10+ chars, Aa, 1, symbol"
          />
          <p className="mt-1 text-xs text-slate-500">{PASSWORD_POLICY_DESCRIPTION}</p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="reset-password-confirm">Confirm password</label>
          <input
            id="reset-password-confirm"
            data-testid="reset-password-confirm-input"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
            placeholder="Repeat new password"
          />
        </div>
        {error ? (
          <p data-testid="reset-password-error" className="text-sm text-rose-600">{error}</p>
        ) : null}
        <button
          data-testid="reset-password-submit-button"
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-sm font-semibold py-2.5 transition"
        >
          {submitting ? "Saving..." : "Save new password"}
        </button>
      </form>
    </AuthLayout>
  );
}

function isAal2PasswordUpdateError(error) {
  const message = String(error?.message || "").toLowerCase();
  return message.includes("aal2") || (message.includes("mfa") && message.includes("password"));
}
